import { useState, useRef, useEffect, useCallback } from "react";
import { Image, Images, X, FileText, Trash2, Film, BarChart3, Plus, Minus, Loader2, ChevronDown } from "lucide-react";
import { AppIcon } from "@/components/ui/app-icon";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { prependOptimisticInfiniteFeedItem } from "@scroll/shared-query";
import { apiRequest, getQueryFn } from "@/lib/queryClient";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { track, trackFeatureUsage } from "@/lib/analytics";
import { getDrafts, saveDraft, deleteDraft, formatRelativeTime, type PostDraft } from "@/lib/drafts";
import MentionAutocomplete from "@/components/MentionAutocomplete";
import { useLinkPreview } from "@/hooks/useLinkPreview";
import { FounderAvatarRing, isFounderUser } from "@/components/FounderAvatarRing";
import { getPastedImageFiles, hasPastedImageData } from "@/lib/clipboard-images";
import { toHeaderSafeFileName } from "@/lib/header-safe-filename";
import ComposeLinkPreviewCard from "@/components/feed/ComposeLinkPreviewCard";
import StockPhotoPicker from "@/components/StockPhotoPicker";
import GifPicker from "@/components/GifPicker";
import {
  buildDraftRestoreState,
  buildComposePoll,
  buildComposePostBody,
  buildOptimisticPostItem,
  buildPostDraft,
  getDraftPollState,
  hasDraftContent,
  normalizeReplyPermission,
  type ReplyPermission,
} from "@scroll/shared-utils";

const MAX_CHARS = 280;
const MAX_IMAGE_SIZE = 10 * 1024 * 1024; // 10MB
const MAX_IMAGES = 4;
const MAX_VIDEO_SIZE = 500 * 1024 * 1024; // 500MB
const FREE_VIDEO_DURATION = 180; // 3 minutes
const PRO_VIDEO_DURATION = 600; // 10 minutes
const POLL_DURATION_OPTIONS = [
  { label: "5 minutes", value: 5 },
  { label: "30 minutes", value: 30 },
  { label: "1 hour", value: 60 },
  { label: "6 hours", value: 360 },
  { label: "1 day", value: 1440 },
  { label: "3 days", value: 4320 },
  { label: "7 days", value: 10080 },
];

/** Poll until a video post's status becomes "ready" or throw on failure/timeout.
 *  Durable worker reconciliation recovers media state when webhook delivery races post creation.
 *  Uses exponential backoff (3s → 15s) to reduce API load during encoding. */
async function waitForVideoReady(postId: string, maxMs = 5 * 60 * 1000) {
  const start = Date.now();
  let attempt = 0;
  let consecutiveErrors = 0;
  while (Date.now() - start < maxMs) {
    const interval = Math.min(3000 * Math.pow(1.5, attempt), 15000);
    await new Promise(r => setTimeout(r, interval));
    attempt++;
    try {
      const res = await apiRequest("GET", `/api/posts/${postId}/video-status`);
      if (res.ok) {
        consecutiveErrors = 0;
        const data = await res.json();
        if (data.videoStatus === "ready") return;
        if (data.videoStatus === "failed") {
          throw new Error("Video processing failed. Please try again.");
        }
      } else {
        consecutiveErrors++;
      }
    } catch (err: any) {
      if (err.message.includes("Video processing failed")) throw err;
      consecutiveErrors++;
    }
    if (consecutiveErrors >= 5) {
      throw new Error("Unable to check video status. Please check your connection and try again.");
    }
  }
  throw new Error("Video processing timed out. Your post will appear once processing completes.");
}

interface ComposeModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialShowDrafts?: boolean;
}

export default function ComposeModal({ isOpen, onClose, initialShowDrafts }: ComposeModalProps) {
  const [content, setContent] = useState("");
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [replyPermission, setReplyPermission] = useState<ReplyPermission>("everyone");
  const [showDrafts, setShowDrafts] = useState(false);
  const [drafts, setDrafts] = useState<PostDraft[]>([]);
  const [activeDraftId, setActiveDraftId] = useState<string | null>(null);
  const [mentionQuery, setMentionQuery] = useState("");
  const [showMentions, setShowMentions] = useState(false);
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [videoPreview, setVideoPreview] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploadingVideo, setIsUploadingVideo] = useState(false);
  // Poll state
  const [showPoll, setShowPoll] = useState(false);
  const [showStockPhotoPicker, setShowStockPhotoPicker] = useState(false);
  const [pollOptions, setPollOptions] = useState<string[]>(["", ""]);
  const [pollDuration, setPollDuration] = useState(1440); // default 1 day
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);
  const { user } = useAuth();
  const maxVideoDuration = user?.subscriptionTier === "pro" || user?.subscriptionTier === "studio" ? PRO_VIDEO_DURATION : FREE_VIDEO_DURATION;
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const linkPreview = useLinkPreview();

  const refreshDrafts = useCallback(() => {
    setDrafts(getDrafts());
  }, []);

  // Load drafts when modal opens
  useEffect(() => {
    if (isOpen) {
      refreshDrafts();
      if (initialShowDrafts) setShowDrafts(true);
    }
  }, [isOpen, initialShowDrafts, refreshDrafts]);

  // Build an optimistic post item to insert into the feed cache immediately
  const buildOptimisticPost = () =>
    buildOptimisticPostItem({
      content,
      imageUrls: imagePreviews,
      user: {
        id: user?.id,
        username: user?.username,
        displayName: user?.displayName,
        profileImageUrl: user?.profileImageUrl,
        isVerified: (user as any)?.isVerified ?? false,
        subscriptionTier: (user as any)?.subscriptionTier ?? null,
      },
    });

  const prependOptimisticPost = (feedKey: string[], optimisticItem: any) => {
    queryClient.setQueryData(feedKey, (old: any) =>
      prependOptimisticInfiniteFeedItem(old, optimisticItem),
    );
  };

  const postMutation = useMutation({
    mutationFn: async () => {
      // Snapshot the bound link before any await — the user could dismiss the
      // preview mid-upload, which would otherwise null it out by send time.
      const embedUrl = linkPreview.boundUrl;
      let imageUrls: string[] = [];
      let muxUploadId: string | null = null;

      // Upload all images sequentially
      if (imageFiles.length > 0) {
        for (const file of imageFiles) {
          const uploadRes = await fetch("/api/posts/upload-image", {
            method: "POST",
            headers: {
              "Content-Type": file.type,
              "X-File-Name": toHeaderSafeFileName(file.name),
            },
            body: file,
            credentials: "include",
          });
          if (!uploadRes.ok) throw new Error("Failed to upload image");
          const uploadData = await uploadRes.json();
          imageUrls.push(uploadData.imageUrl);
        }
      }

      if (videoFile) {
        // Step 1: Get Mux upload URL
        const uploadRes = await apiRequest("POST", "/api/posts/upload-video", {});
        if (!uploadRes.ok) throw new Error("Failed to create video upload");
        const { uploadId, uploadUrl } = await uploadRes.json();
        muxUploadId = uploadId;

        // Step 2: Create the post first (with muxUploadId)
        const response = await apiRequest(
          "POST",
          "/api/posts",
          buildComposePostBody({
            content,
            replyPermission,
            embedUrl,
            extra: { muxUploadId },
          }),
        );
        if (!response.ok) throw new Error("Failed to create post");
        const postData = await response.json();

        // Step 3: Upload video binary to Mux via XHR (with progress)
        setIsUploadingVideo(true);
        try {
          await new Promise<void>((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            xhr.upload.addEventListener("progress", (e) => {
              if (e.lengthComputable) {
                setUploadProgress(Math.round((e.loaded / e.total) * 100));
              }
            });
            xhr.addEventListener("load", () => {
              if (xhr.status >= 200 && xhr.status < 300) resolve();
              else reject(new Error("Video upload failed"));
            });
            xhr.addEventListener("error", () => reject(new Error("Video upload failed")));
            xhr.open("PUT", uploadUrl);
            xhr.send(videoFile);
          });
        } finally {
          setIsUploadingVideo(false);
        }

        // Wait for Mux to finish processing the video
        await waitForVideoReady(postData.postId || postData.id);

        return postData;
      }

      const response = await apiRequest(
        "POST",
        "/api/posts",
        buildComposePostBody({
          content,
          replyPermission,
          imageUrls,
          embedUrl: linkPreview.boundUrl,
          poll: buildComposePoll({
            enabled: showPoll,
            options: pollOptions,
            // pollDuration state is minutes (POLL_DURATION_OPTIONS); the API expects seconds
            duration: pollDuration * 60,
          }),
        }),
      );
      if (!response.ok) throw new Error("Failed to create post");
      return response.json();
    },
    onMutate: async () => {
      // Skip optimistic update for video posts (they need server processing)
      if (videoFile) return {};

      await queryClient.cancelQueries({ queryKey: ["/api/feed"] });
      await queryClient.cancelQueries({ queryKey: ["/api/feed/following"] });

      const previousFeed = queryClient.getQueryData(["/api/feed"]);
      const previousFollowing = queryClient.getQueryData(["/api/feed/following"]);

      const optimisticItem = buildOptimisticPost();
      prependOptimisticPost(["/api/feed"], optimisticItem);
      prependOptimisticPost(["/api/feed/following"], optimisticItem);

      return { previousFeed, previousFollowing };
    },
    onSuccess: (data, _vars, context) => {
      track("post_created", { postId: data?.postId });
      trackFeatureUsage("compose_post", "post_created", {
        postId: data?.postId,
        hasImages: imageFiles.length > 0,
        hasVideo: !!videoFile,
        hasGif: imageFiles.some((file) => file.type === "image/gif"),
        hasPoll: showPoll && pollOptions.some((option) => option.trim().length > 0),
      });
      // Delete the active draft after successful post
      if (activeDraftId) {
        deleteDraft(activeDraftId);
      }
      resetAndClose();
    },
    onError: (_err, _vars, context) => {
      if (context?.previousFeed) queryClient.setQueryData(["/api/feed"], context.previousFeed);
      if (context?.previousFollowing) queryClient.setQueryData(["/api/feed/following"], context.previousFollowing);
      const msg = _err instanceof Error ? _err.message : "Failed to create post";
      if (/episode_limit/.test(msg)) {
        toast({
          title: "Episode Limit Reached",
          description: "Free accounts can publish 5 episodes each calendar month. Upgrade to Pro for unlimited publishing.",
          variant: "destructive",
        });
      } else {
        toast({ title: "Post failed", description: msg, variant: "destructive" });
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/feed"] });
      queryClient.invalidateQueries({ queryKey: ["/api/feed/following"] });
      if (user?.id) {
        queryClient.invalidateQueries({ queryKey: [`/api/users/${user.id}/posts`] });
      }
    },
  });

  // Derived: video is processing when mutation is pending but upload phase is done
  const isProcessingVideo = postMutation.isPending && !isUploadingVideo && !!videoFile;

  const handleClose = () => {
    if (
      hasDraftContent({
        content,
        imageCount: imagePreviews.length,
        hasVideo: Boolean(videoFile),
        showPoll,
      })
    ) {
      saveDraft(
        buildPostDraft({
          content,
          imageDataUrl: imagePreviews[0] || null,
          replyPermission,
          showPoll,
          pollOptions,
          pollDuration,
        }),
        activeDraftId || undefined,
      );
      // Notify sidebar of draft count change
      window.dispatchEvent(new Event("draftsChanged"));
    }
    resetState();
    onClose();
  };

  const resetAndClose = () => {
    resetState();
    window.dispatchEvent(new Event("draftsChanged"));
    onClose();
  };

  const resetState = () => {
    setContent("");
    setImagePreviews([]);
    setImageFiles([]);
    setReplyPermission("everyone");
    setShowDrafts(false);
    setActiveDraftId(null);
    setShowMentions(false);
    clearVideo();
    setIsUploadingVideo(false);
    setShowPoll(false);
    setPollOptions(["", ""]);
    setPollDuration(1440);
    linkPreview.reset();
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // Auto-focus textarea on open
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => textareaRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Close on Escape (skip if mention dropdown is open — it handles Escape itself)
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !showMentions) handleClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, content, imagePreviews, replyPermission, activeDraftId, showMentions]);

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    if (value.length <= MAX_CHARS) {
      setContent(value);
      linkPreview.detectAndFetch(value);
    }
    // Detect @mention trigger
    const cursorPos = e.target.selectionStart || 0;
    const textBeforeCursor = value.slice(0, cursorPos);
    const mentionMatch = textBeforeCursor.match(/@(\w*)$/);
    if (mentionMatch) {
      setMentionQuery(mentionMatch[1]);
      setShowMentions(true);
    } else {
      setShowMentions(false);
    }
    // Auto-resize
    const textarea = e.target;
    textarea.style.height = "auto";
    textarea.style.height = `${textarea.scrollHeight}px`;
  };

  const handlePaste = async (e: React.ClipboardEvent) => {
    if (hasPastedImageData(e.clipboardData)) e.preventDefault();
    const pastedImages = await getPastedImageFiles(e.clipboardData);
    if (pastedImages.length > 0) addImageFiles(pastedImages);

    setTimeout(() => {
      const value = textareaRef.current?.value || "";
      linkPreview.detectAndFetch(value);
    }, 0);
  };

  const handleMentionSelect = (username: string) => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    const cursorPos = textarea.selectionStart || 0;
    const textBeforeCursor = content.slice(0, cursorPos);
    const atIndex = textBeforeCursor.lastIndexOf("@");
    const before = content.slice(0, atIndex);
    const after = content.slice(cursorPos);
    const newContent = `${before}@${username} ${after}`;
    if (newContent.length <= MAX_CHARS) {
      setContent(newContent);
      setTimeout(() => {
        const newPos = atIndex + username.length + 2;
        textarea.selectionStart = textarea.selectionEnd = newPos;
        textarea.focus();
      }, 0);
    }
    setShowMentions(false);
  };

  const addImageFiles = useCallback((files: File[]) => {
    // Filter valid image files
    const validFiles = files.filter(
      (f) => f.type.startsWith("image/") && f.size <= MAX_IMAGE_SIZE
    );
    if (validFiles.length === 0) return false;

    // Enforce max images
    const remaining = MAX_IMAGES - imageFiles.length;
    const filesToAdd = validFiles.slice(0, remaining);
    if (filesToAdd.length === 0) return false;

    // Clear video and poll (mutual exclusivity)
    clearVideo();
    clearPoll();

    setImageFiles((prev) => [...prev, ...filesToAdd]);

    // Generate previews
    filesToAdd.forEach((file) => {
      const reader = new FileReader();
      reader.onload = () => {
        setImagePreviews((prev) => [...prev, reader.result as string]);
      };
      reader.readAsDataURL(file);
    });

    return true;
  }, [imageFiles.length, videoPreview]);

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    addImageFiles(files);
  };

  const handleGifSelect = async (gifUrl: string) => {
    try {
      const resp = await fetch(gifUrl);
      const blob = await resp.blob();
      const ext = blob.type.split("/")[1] || "gif";
      const file = new File([blob], `gif.${ext}`, { type: blob.type });
      // GIF is mutually exclusive with other images, video, and polls (matches Expo).
      // clearImages() first cancels any in-flight FileReader.onload that would otherwise
      // append to imagePreviews after we replace it below.
      clearImages();
      clearVideo();
      clearPoll();
      setImageFiles([file]);
      const reader = new FileReader();
      reader.onload = () => setImagePreviews([reader.result as string]);
      reader.readAsDataURL(file);
    } catch (err) {
      console.error("GIF fetch failed:", err);
      toast({ title: "Couldn't load GIF", description: "Please try again.", variant: "destructive" });
    }
  };

  const handleStockPhotoSelect = async (imageUrl: string) => {
    if (imageFiles.length >= MAX_IMAGES) return;
    setShowStockPhotoPicker(false);
    try {
      const resp = await fetch(imageUrl);
      const blob = await resp.blob();
      const ext = blob.type.split("/")[1] || "jpeg";
      const file = new File([blob], `stock-photo.${ext}`, { type: blob.type });
      clearVideo();
      clearPoll();
      setImageFiles((prev) => [...prev, file]);
      const reader = new FileReader();
      reader.onload = () => {
        setImagePreviews((prev) => [...prev, reader.result as string]);
      };
      reader.readAsDataURL(file);
    } catch {
      // silently fail
    }
  };

  const removeImage = (index: number) => {
    setImageFiles((prev) => prev.filter((_, i) => i !== index));
    setImagePreviews((prev) => prev.filter((_, i) => i !== index));
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const clearImages = () => {
    setImagePreviews([]);
    setImageFiles([]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleVideoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("video/")) return;
    if (file.size > MAX_VIDEO_SIZE) {
      alert("Video must be under 500MB");
      return;
    }

    // Check duration using a temporary video element
    const url = URL.createObjectURL(file);
    const video = document.createElement("video");
    video.preload = "metadata";
    video.onerror = () => {
      URL.revokeObjectURL(url);
    };
    video.onloadedmetadata = () => {
      URL.revokeObjectURL(url);
      if (video.duration > maxVideoDuration) {
        alert(
          (user?.subscriptionTier === "pro" || user?.subscriptionTier === "studio")
            ? `Video must be under ${maxVideoDuration / 60} minutes.`
            : `Free accounts can upload up to ${maxVideoDuration / 60} minutes. Upgrade to Scroll Pro for up to 10 minutes.`
        );
        return;
      }
      // Clear images and poll (mutual exclusivity)
      clearImages();
      clearPoll();
      setVideoFile(file);
      setVideoPreview(URL.createObjectURL(file));
    };
    video.src = url;
  };

  const clearVideo = () => {
    if (videoPreview) URL.revokeObjectURL(videoPreview);
    setVideoFile(null);
    setVideoPreview(null);
    setUploadProgress(0);
    if (videoInputRef.current) videoInputRef.current.value = "";
  };

  const togglePoll = () => {
    if (showPoll) {
      clearPoll();
    } else {
      // Clear images and video (mutual exclusivity)
      clearImages();
      clearVideo();
      setShowPoll(true);
      setPollOptions(["", ""]);
      setPollDuration(1440);
    }
  };

  const clearPoll = () => {
    setShowPoll(false);
    setPollOptions(["", ""]);
    setPollDuration(1440);
  };

  const addPollOption = () => {
    if (pollOptions.length < 4) {
      setPollOptions([...pollOptions, ""]);
    }
  };

  const removePollOption = (index: number) => {
    if (pollOptions.length > 2) {
      setPollOptions(pollOptions.filter((_, i) => i !== index));
    }
  };

  const updatePollOption = (index: number, value: string) => {
    const updated = [...pollOptions];
    updated[index] = value;
    setPollOptions(updated);
  };

  const loadDraft = (draft: PostDraft) => {
    const restoreState = buildDraftRestoreState({
      draft,
      defaultPollDuration: 1440,
    });
    setContent(restoreState.content);
    setReplyPermission(restoreState.replyPermission);
    setActiveDraftId(draft.id);
    clearVideo();
    if (restoreState.imageDataUrl) {
      setImagePreviews([restoreState.imageDataUrl]);
      // Convert base64 data URL back to a File so the post mutation can include it
      try {
        const [header, base64] = restoreState.imageDataUrl.split(",");
        const mime = header.match(/:(.*?);/)?.[1] || "image/png";
        const binary = atob(base64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
        setImageFiles([new File([bytes], `draft-image.${mime.split("/")[1]}`, { type: mime })]);
      } catch {
        setImageFiles([]);
      }
    } else {
      setImagePreviews([]);
      setImageFiles([]);
    }
    setShowPoll(restoreState.pollState.showPoll);
    setPollOptions(restoreState.pollState.pollOptions);
    setPollDuration(restoreState.pollState.pollDuration);
    setShowDrafts(false);
    setTimeout(() => textareaRef.current?.focus(), 50);
  };

  const handleDeleteDraft = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    deleteDraft(id);
    refreshDrafts();
    if (activeDraftId === id) {
      setActiveDraftId(null);
    }
    window.dispatchEvent(new Event("draftsChanged"));
  };

  if (!isOpen) return null;

  const displayName = user?.displayName || user?.username || "You";
  const avatarUrl = user?.profileImageUrl;
  const charCount = content.length;
  const validPollOptions = pollOptions.filter((o) => o.trim()).length;
  const canPost =
    (content.trim().length > 0 || imageFiles.length > 0 || !!videoFile || (showPoll && validPollOptions >= 2) || !!linkPreview.boundUrl) &&
    !postMutation.isPending &&
    !isUploadingVideo &&
    !isProcessingVideo;

  return (
    <div className="fixed inset-0 z-60 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 scroll-modal-overlay animate-fade-in"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-[560px] bg-(--butter-bg-elevated) rounded-[var(--butter-radius-surface)] butter-shadow-panel animate-compose-in">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-(--scroll-border)">
          <button
            onClick={handleClose}
            className="w-8 h-8 flex items-center justify-center rounded-full text-(--sl-text) hover:bg-(--scroll-bg-tertiary) transition-colors"
          >
            <X size={20} />
          </button>
          <span className="scroll-heading text-base">New post</span>
          <button
            onClick={() => postMutation.mutate()}
            disabled={!canPost}
            className={`py-1.5 px-5 rounded-full bg-(--scroll-text) text-(--scroll-bg) text-sm font-semibold transition-opacity ${isProcessingVideo ? 'opacity-80' : 'disabled:opacity-30'}`}
          >
            {isProcessingVideo ? "Processing video…" : isUploadingVideo ? `Uploading... ${uploadProgress}%` : postMutation.isPending ? "Posting..." : "Post"}
          </button>
        </div>

        {/* Body */}
        <div className="p-5">
          <div className="flex gap-3">
            {/* Avatar */}
            <FounderAvatarRing isFounder={isFounderUser(user?.id)}>
              <div className="shrink-0 w-10 h-10 rounded-full overflow-hidden">
                {avatarUrl ? (
                  <img src={avatarUrl} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-muted font-heading font-semibold text-xs text-muted-foreground">
                    {displayName.charAt(0).toUpperCase()}
                  </div>
                )}
              </div>
            </FounderAvatarRing>

            {/* Input */}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-(--sl-text) mb-1">{displayName}</p>
              <div className="relative">
                <textarea
                  ref={textareaRef}
                  className="w-full border-none outline-hidden resize-none text-base leading-relaxed bg-transparent text-(--sl-text) min-h-[80px] font-[inherit] placeholder:text-(--sl-text-tertiary)"
                  placeholder="What's on your mind?"
                  value={content}
                  onChange={handleTextareaChange}
                  onPaste={handlePaste}
                  maxLength={MAX_CHARS}
                  rows={3}
                />
                <MentionAutocomplete
                  query={mentionQuery}
                  visible={showMentions}
                  onSelect={handleMentionSelect}
                  onClose={() => setShowMentions(false)}
                />
              </div>

              {/* Link preview */}
              {linkPreview.isLoading && (
                <div className="flex items-center gap-2 mt-2 text-xs text-(--sl-text-tertiary)">
                  <AppIcon icon={Loader2} size="xs" className="animate-spin" />
                  Fetching link preview…
                </div>
              )}
              {linkPreview.preview && (
                <ComposeLinkPreviewCard preview={linkPreview.preview} onDismiss={linkPreview.dismiss} />
              )}

              {/* Multi-image previews */}
              {imagePreviews.length > 0 && (
                <div className="flex gap-2 mt-2 overflow-x-auto pb-1">
                  {imagePreviews.map((preview, index) => (
                    <div key={index} className="relative shrink-0">
                      <img
                        src={preview}
                        alt=""
                        className="h-32 w-32 rounded-lg object-cover border border-border"
                      />
                      <button
                        className="absolute top-1 right-1 bg-black/70 hover:bg-black/80 text-white rounded-full p-0.5 transition-colors"
                        onClick={() => removeImage(index)}
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {videoPreview && (
                <div className="relative mt-2 inline-block max-w-full">
                  <video
                    src={videoPreview}
                    className="max-w-full max-h-48 rounded-lg object-cover border border-border"
                    autoPlay
                    muted
                    loop
                    playsInline
                  />
                  <button
                    className="absolute top-1.5 right-1.5 bg-black/70 hover:bg-black/80 text-white rounded-full p-1 transition-colors"
                    onClick={clearVideo}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              )}

              {/* Poll builder */}
              {showPoll && (
                <div className="mt-3 border border-(--scroll-border) rounded-xl p-3 space-y-2">
                  {pollOptions.map((option, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <input
                        type="text"
                        value={option}
                        onChange={(e) => updatePollOption(index, e.target.value)}
                        placeholder={`Option ${index + 1}`}
                        maxLength={50}
                        className="flex-1 scroll-input text-sm py-1.5 px-3"
                      />
                      {pollOptions.length > 2 && (
                        <button
                          type="button"
                          onClick={() => removePollOption(index)}
                          className="w-7 h-7 flex items-center justify-center rounded-full text-(--sl-text-tertiary) hover:text-red-500 hover:bg-(--scroll-bg-tertiary) transition-colors"
                        >
                          <AppIcon icon={Minus} size="xs" />
                        </button>
                      )}
                    </div>
                  ))}

                  <div className="flex items-center justify-between pt-1">
                    {pollOptions.length < 4 && (
                      <button
                        type="button"
                        onClick={addPollOption}
                        className="flex items-center gap-1 text-xs text-(--sl-text-secondary) hover:text-(--sl-text) transition-colors"
                      >
                        <AppIcon icon={Plus} size="xs" />
                        Add option
                      </button>
                    )}

                    <div className="flex items-center gap-2 ml-auto">
                      <label className="text-xs text-(--sl-text-tertiary)">Duration:</label>
                      <select
                        value={pollDuration}
                        onChange={(e) => setPollDuration(Number(e.target.value))}
                        className="text-xs text-(--sl-text-secondary) bg-transparent border border-(--scroll-border) rounded-full px-2 py-1 outline-hidden cursor-pointer"
                      >
                        {POLL_DURATION_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {showPoll && validPollOptions < 2 && (
                    <p className="text-xs text-(--sl-text-tertiary)">
                      Add at least 2 options to create a poll
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-(--scroll-border)">
          {/* Row 1: Media actions + character count */}
          <div className="flex items-center justify-between px-5 h-12">
            <div className="flex items-center gap-4">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageSelect}
                hidden
              />
              <input
                ref={videoInputRef}
                type="file"
                accept="video/*"
                onChange={handleVideoSelect}
                hidden
              />
              <button
                className={`p-1 transition-colors ${
                  imageFiles.length > 0
                    ? "text-(--scroll-text)"
                    : "text-(--sl-text-secondary) hover:text-(--scroll-text)"
                }`}
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={!!videoFile || showPoll}
                title={imageFiles.length > 0 ? `${imageFiles.length}/${MAX_IMAGES} images` : "Add images"}
              >
                <Image size={20} />
              </button>
              <button
                className={`p-1 transition-colors ${
                  videoFile
                    ? "text-(--scroll-text)"
                    : "text-(--sl-text-secondary) hover:text-(--scroll-text)"
                }`}
                type="button"
                onClick={() => videoInputRef.current?.click()}
                disabled={imageFiles.length > 0 || showPoll}
                title="Add video"
              >
                <Film size={20} />
              </button>
              <GifPicker onSelect={handleGifSelect}>
                <button
                  className="p-1 text-(--sl-text-secondary) hover:text-(--scroll-text) transition-colors disabled:opacity-30"
                  type="button"
                  disabled={!!videoFile || showPoll}
                  title="GIF"
                >
                  <span className="text-sm font-bold">GIF</span>
                </button>
              </GifPicker>
              <button
                className="p-1 text-(--sl-text-secondary) hover:text-(--scroll-text) transition-colors disabled:opacity-30"
                type="button"
                onClick={() => setShowStockPhotoPicker(true)}
                disabled={!!videoFile || showPoll}
                title="Stock photos"
              >
                <Images size={20} />
              </button>
              <button
                className={`p-1 transition-colors ${
                  showPoll
                    ? "text-(--scroll-text)"
                    : "text-(--sl-text-secondary) hover:text-(--scroll-text)"
                }`}
                type="button"
                onClick={togglePoll}
                disabled={imageFiles.length > 0 || !!videoFile}
                title="Add poll"
              >
                <BarChart3 size={20} />
              </button>
            </div>
            {charCount > 0 && (
              <span className={`text-sm ${charCount > MAX_CHARS - 20 ? "text-red-500" : "text-muted-foreground"}`}>
                {charCount}/{MAX_CHARS}
              </span>
            )}
          </div>

          {/* Divider */}
          <div className="h-px bg-(--scroll-border)" />

          {/* Row 2: draft access and reply permissions */}
          <div className="flex items-center justify-between px-5 py-2">
            <div className="flex items-center gap-2.5">
              {/* Drafts button */}
              {drafts.length > 0 && (
                <button
                  type="button"
                  onClick={() => setShowDrafts(!showDrafts)}
                  className="flex items-center gap-1.5 text-xs text-(--sl-text-secondary) border border-(--scroll-border) rounded-full px-2.5 py-1 hover:bg-(--scroll-bg-tertiary) transition-colors"
                >
                  <AppIcon icon={FileText} size="xs" />
                  Drafts ({drafts.length})
                </button>
              )}
            </div>

            {/* Reply permission selector */}
            <div className="relative">
              <select
                value={replyPermission}
                onChange={(e) => setReplyPermission(normalizeReplyPermission(e.target.value))}
                className="appearance-none text-xs text-(--sl-text-secondary) bg-transparent border border-(--scroll-border) rounded-full pl-2.5 pr-7 py-1 outline-hidden cursor-pointer hover:bg-(--scroll-bg-tertiary) transition-colors"
              >
                <option value="everyone">Everyone can reply</option>
                <option value="following">People you follow</option>
                <option value="mentioned">Mentioned only</option>
                <option value="none">No replies</option>
              </select>
              <ChevronDown size={12} aria-hidden="true" className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-(--sl-text-secondary)" />
            </div>
          </div>
        </div>

        {/* Drafts panel */}
        {showDrafts && drafts.length > 0 && (
          <div className="border-t border-(--scroll-border) max-h-[200px] overflow-y-auto">
            {drafts.slice().reverse().map((draft) => (
              <button
                key={draft.id}
                type="button"
                onClick={() => loadDraft(draft)}
                className={`w-full text-left px-5 py-3 hover:bg-(--scroll-bg-tertiary) transition-colors flex items-center gap-3 group ${
                  activeDraftId === draft.id ? "bg-(--scroll-bg-tertiary)" : ""
                }`}
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-(--sl-text) truncate">
                    {draft.content.slice(0, 50)}
                    {draft.content.length > 50 ? "..." : ""}
                  </p>
                  <p className="text-xs text-(--sl-text-tertiary) mt-0.5">
                    {formatRelativeTime(draft.updatedAt)}
                    {draft.imageDataUrl ? " · has image" : ""}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={(e) => handleDeleteDraft(e, draft.id)}
                  className="shrink-0 w-7 h-7 flex items-center justify-center rounded-full text-(--sl-text-tertiary) opacity-0 group-hover:opacity-100 hover:bg-(--scroll-bg-tertiary) hover:text-red-500 transition-all"
                >
                  <AppIcon icon={Trash2} size="xs" />
                </button>
              </button>
            ))}
          </div>
        )}
      </div>
      <StockPhotoPicker
        open={showStockPhotoPicker}
        onSelect={handleStockPhotoSelect}
        onClose={() => setShowStockPhotoPicker(false)}
      />
    </div>
  );
}
